import { ModelError_Item } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useRef, useState } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { ShakyErrorIndicator } from '~components/ErrorIndicators/ShakyErrorIndicator';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import {
  UserForm,
  UserFormHandler,
  UserValidationDTOWithSitesDiff,
} from '~components/UserForm/v2/UserForm';
import { CompanyShare } from '~hooks/useCompanyShares/models';
import { Driver } from '~hooks/useDrivers/models';

interface DriverEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: UserValidationDTOWithSitesDiff) => Promise<void>;
  driver?: Driver;
  companyShares?: CompanyShare[];
  asyncErrors?: ModelError_Item[];
  isUpdating: boolean;
  companyId?: string;
  managedCompanyId?: string;
}

export const DriverEditDialog = ({
  isOpen,
  onClose,
  onSubmit,
  driver,
  companyShares = [],
  asyncErrors = [],
  isUpdating,
  companyId,
  managedCompanyId,
}: DriverEditDialogProps) => {
  const updateUserDialogRef = useRef<UserFormHandler>(null);
  const [isDirty, setIsDirty] = useState(false);

  const handleSubmit = async () => {
    const userData = await new Promise<UserValidationDTOWithSitesDiff | undefined>(
      (resolve) => {
        updateUserDialogRef.current?.submit((data) => resolve(data));
      },
    );

    if (userData) {
      await onSubmit(userData);
    }
  };

  return (
    <Dialog isOpen={isOpen} preventCloseOnBackdropClick preventCloseOnEscapeKeyDown>
      <DialogHeader onClose={onClose}>{t('user.update_driver')}</DialogHeader>

      <DialogContent>
        <UserForm
          ref={updateUserDialogRef}
          allowSharing
          availableAuthMethods={[]}
          companyShares={companyShares}
          defaultDriver={driver}
          defaultUser={null}
          editable
          errors={asyncErrors}
          onFormStateChange={({ isDirty }) => setIsDirty(isDirty)}
          scopedCompanyId={companyId}
          managedCompanyId={managedCompanyId}
        />
      </DialogContent>

      <DialogActions>
        {Boolean(asyncErrors.length) && (
          <ShakyErrorIndicator
            message={t('administration.users.user_form.invalid_data_error_messages')}
          />
        )}

        <SmallButton
          color="secondary"
          disabled={isUpdating}
          onClick={onClose}
          variant="outlined"
        >
          {t('actions.cancel')}
        </SmallButton>

        <SmallLoadingButton
          disabled={!isDirty}
          loading={isUpdating}
          onClick={handleSubmit}
        >
          {t('actions.update')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
};
